/**
 * Shared API client for the NestJS backend (Sprint: Frontend → NestJS).
 *
 * Base URL comes from VITE_API_BASE_URL (see .env.example); the fallback
 * targets the local NestJS dev server. Paths below include the /api prefix
 * (the backend applies a global `api` prefix).
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:4030";
const ACCESS_TOKEN_KEY = "crm_access_token";

export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token) {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
}

export function clearAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(message, { status, code, fieldErrors } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors || null;
  }
}

/** Marks features the backend does not expose yet (Phase 10 — no fabricated data). */
export class ApiNotAvailableError extends ApiError {
  constructor(feature) {
    super(`${feature} is not available from the backend yet.`, { status: 501, code: "NOT_AVAILABLE" });
    this.name = "ApiNotAvailableError";
  }
}

function decodeJwtPayload(token) {
  if (!token) return null;

  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

function getInitials(fullName) {
  if (!fullName) return "U";

  const parts = fullName.split(" ").filter(Boolean);
  if (!parts.length) return "U";

  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
}

/**
 * Current user from the NestJS JWT claims (Part 61 §9): sub, email, name,
 * role, orgId. The old ASP.NET role claim URI is no longer emitted — the
 * NestJS JWT carries a plain `role` claim.
 */
export function getCurrentUser() {
  const payload = decodeJwtPayload(getAccessToken());
  if (!payload) return null;

  const fullName = payload?.name || payload?.fullName || "User";

  return {
    sub: payload?.sub || payload?.userId || null,
    fullName,
    shortName: fullName.split(" ")[0] || fullName,
    email: payload?.email || "",
    role: payload?.role || "User",
    orgId: payload?.orgId || null,
    initials: getInitials(fullName),
  };
}

/** Session expired — clear token and land the user on login (Phase 4). */
function handleSessionExpired() {
  clearAccessToken();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/**
 * Central request wrapper. Normalizes the NestJS error envelope
 * { status, code, message, fieldErrors } (Part 72 §72.0) and handles 401.
 */
export async function apiRequest(path, options = {}) {
  const token = getAccessToken();
  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // FormData sets its own multipart Content-Type (Sprint 7 batch import).
  if (typeof FormData !== "undefined" && options.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
    });
  } catch (networkError) {
    throw new ApiError(
      "Cannot reach the backend. Check that the NestJS server is running and VITE_API_BASE_URL is correct.",
      { status: 0, code: "NETWORK_ERROR" }
    );
  }

  if (!response.ok) {
    let status = response.status;
    let code = null;
    let message = `Request failed: ${response.status}`;
    let fieldErrors = null;

    try {
      const error = await response.json();
      if (error?.message) message = error.message;
      if (error?.code) code = error.code;
      if (error?.status) status = error.status;
      if (error?.fieldErrors) fieldErrors = error.fieldErrors;
    } catch {
      // Non-JSON error body — keep the generic message.
    }

    if (status === 401) {
      handleSessionExpired();
    }

    throw new ApiError(message, { status, code, fieldErrors });
  }

  if (response.status === 204) {
    return null;
  }

  return response.json();
}

export const API_BASE = API_BASE_URL;
